const UNSAFE_ELEMENTS = new Set(['script', 'style']);
const NAMED_ENTITIES = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: '\u00a0',
  quot: '"',
};

function normalizeText(value) {
  return String(value ?? '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function isLabelOnly(text) {
  return text.length <= 40 && /:$/.test(text);
}

function decodeEntities(value) {
  return value.replace(/&(#(?:x[0-9a-f]+|\d+)|[a-z][a-z0-9]+);/gi, (entity, reference) => {
    if (reference[0] === '#') {
      const hexadecimal = reference[1]?.toLowerCase() === 'x';
      const number = Number.parseInt(reference.slice(hexadecimal ? 2 : 1), hexadecimal ? 16 : 10);
      if (Number.isInteger(number) && number >= 0 && number <= 0x10ffff) {
        try {
          return String.fromCodePoint(number);
        } catch {
          return entity;
        }
      }
      return entity;
    }

    return NAMED_ENTITIES[reference.toLowerCase()] ?? entity;
  });
}

function findTagEnd(source, start) {
  let quote = '';
  for (let index = start + 1; index < source.length; index += 1) {
    const character = source[index];
    if (quote) {
      if (character === quote) quote = '';
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
    } else if (character === '>') {
      return index;
    }
  }
  return -1;
}

function extractWithoutDOMParser(source) {
  const paragraphs = [];
  const fallback = [];
  let currentParagraph = null;
  let paragraphCount = 0;
  let index = 0;

  const appendText = (text) => {
    const decoded = decodeEntities(text);
    fallback.push(decoded);
    if (currentParagraph) currentParagraph.push(decoded);
  };

  const finishParagraph = () => {
    if (!currentParagraph) return;
    const text = normalizeText(currentParagraph.join(''));
    if (text && !isLabelOnly(text)) paragraphs.push(text);
    currentParagraph = null;
  };

  while (index < source.length) {
    if (source[index] !== '<') {
      const nextTag = source.indexOf('<', index);
      const end = nextTag === -1 ? source.length : nextTag;
      appendText(source.slice(index, end));
      index = end;
      continue;
    }

    if (source.startsWith('<!--', index)) {
      const commentEnd = source.indexOf('-->', index + 4);
      index = commentEnd === -1 ? source.length : commentEnd + 3;
      continue;
    }

    const tagEnd = findTagEnd(source, index);
    if (tagEnd === -1) {
      appendText(source.slice(index));
      break;
    }

    const tagMarkup = source.slice(index + 1, tagEnd);
    const tagMatch = tagMarkup.match(/^\s*(\/?)\s*([a-z][a-z0-9:-]*)/i);
    if (!tagMatch) {
      appendText(source.slice(index, tagEnd + 1));
      index = tagEnd + 1;
      continue;
    }

    const closing = tagMatch[1] === '/';
    const tagName = tagMatch[2].toLowerCase();
    const selfClosing = /\/\s*$/.test(tagMarkup);

    if (!closing && UNSAFE_ELEMENTS.has(tagName)) {
      const closingPattern = new RegExp(`<\\/\\s*${tagName}\\s*>`, 'gi');
      closingPattern.lastIndex = tagEnd + 1;
      const closingMatch = closingPattern.exec(source);
      index = closingMatch ? closingPattern.lastIndex : source.length;
      continue;
    }

    if (!closing && tagName === 'br') appendText(' ');

    if (tagName === 'p') {
      if (closing) {
        finishParagraph();
      } else {
        finishParagraph();
        paragraphCount += 1;
        currentParagraph = [];
        if (selfClosing) finishParagraph();
      }
    }

    index = tagEnd + 1;
  }

  finishParagraph();
  if (paragraphs.length) return paragraphs[0];
  return paragraphCount === 0 ? normalizeText(fallback.join('')) : '';
}

function textWithBreakBoundaries(element) {
  element?.querySelectorAll?.('br').forEach((lineBreak) => {
    lineBreak.replaceWith(lineBreak.ownerDocument.createTextNode(' '));
  });
  return normalizeText(element?.textContent);
}

function extractWithDOMParser(source, Parser) {
  const document = new Parser().parseFromString(source, 'text/html');
  document.querySelectorAll('script, style').forEach((element) => element.remove());
  const paragraphs = document.querySelectorAll('p');

  for (const paragraph of paragraphs) {
    const text = textWithBreakBoundaries(paragraph);
    if (text && !isLabelOnly(text)) return text;
  }

  if (paragraphs.length > 0) return '';
  return textWithBreakBoundaries(document.body);
}

export function extractFirstParagraph(description, Parser = globalThis.DOMParser) {
  if (typeof description !== 'string' || description === '') return '';
  return typeof Parser === 'function'
    ? extractWithDOMParser(description, Parser)
    : extractWithoutDOMParser(description);
}

export function renderFirstParagraph(element) {
  const output = element.querySelector('[data-first-paragraph-output]');
  if (!output) return;

  const text = extractFirstParagraph(element.dataset.description ?? '');
  const designMode = element.dataset.designMode === 'true' || globalThis.Shopify?.designMode === true;
  const placeholder = designMode && text === '' ? element.dataset.placeholder ?? '' : '';
  const presentation = text || placeholder;
  const hidden = presentation === '';

  if (output.textContent !== presentation) output.textContent = presentation;
  if (output.hidden !== hidden) output.hidden = hidden;
  if (element.hidden !== hidden) element.hidden = hidden;
  const showPlaceholder = placeholder !== '';
  if (element.classList?.contains?.('em-first-paragraph--placeholder') !== showPlaceholder) {
    element.classList?.toggle('em-first-paragraph--placeholder', showPlaceholder);
  }
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class EmFirstParagraph extends HTMLElementBase {
  static observedAttributes = ['data-description', 'data-design-mode', 'data-placeholder'];

  constructor() {
    super();
    this.lifecycleConnected = false;
    this.lifecycleObserver = null;
    this.renderingFirstParagraph = false;
    this.lastRenderState = null;
  }

  connectedCallback() {
    if (this.lifecycleConnected) return;
    this.lifecycleConnected = true;
    this.render();

    if (typeof globalThis.MutationObserver !== 'function') return;
    this.lifecycleObserver ??= new globalThis.MutationObserver((records) => {
      const output = this.querySelector('[data-first-paragraph-output]');
      const outputWasMutated = records.some((record) => {
        if (record.type === 'childList' && record.target === this) return true;
        if (!output || (record.type !== 'childList' && record.type !== 'characterData')) return false;
        return record.target === output || output.contains?.(record.target) === true;
      });
      if (
        this.lifecycleConnected &&
        !this.renderingFirstParagraph &&
        outputWasMutated
      ) {
        this.render();
      }
    });
    this.lifecycleObserver.observe(this, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    if (!this.lifecycleConnected) return;
    this.lifecycleConnected = false;
    this.lifecycleObserver?.disconnect();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.lifecycleConnected && oldValue !== newValue) this.render();
  }

  render() {
    if (this.renderingFirstParagraph) return;

    const output = this.querySelector('[data-first-paragraph-output]');
    const state = {
      description: this.dataset.description ?? '',
      designMode: this.dataset.designMode ?? '',
      globalDesignMode: globalThis.Shopify?.designMode === true,
      hidden: this.hidden,
      output,
      outputHidden: output?.hidden,
      outputText: output?.textContent,
      placeholder: this.dataset.placeholder ?? '',
    };
    const previous = this.lastRenderState;
    if (
      previous &&
      previous.description === state.description &&
      previous.designMode === state.designMode &&
      previous.globalDesignMode === state.globalDesignMode &&
      previous.hidden === state.hidden &&
      previous.output === state.output &&
      previous.outputHidden === state.outputHidden &&
      previous.outputText === state.outputText &&
      previous.placeholder === state.placeholder
    ) {
      return;
    }

    this.renderingFirstParagraph = true;
    try {
      renderFirstParagraph(this);
      this.lastRenderState = {
        ...state,
        hidden: this.hidden,
        outputHidden: output?.hidden,
        outputText: output?.textContent,
      };
    } finally {
      this.renderingFirstParagraph = false;
    }
  }
}

export function initializeFirstParagraphs(root = globalThis.document) {
  const elements = [];
  if (root?.matches?.('em-first-paragraph')) elements.push(root);
  root?.querySelectorAll?.('em-first-paragraph').forEach((element) => elements.push(element));
  elements.forEach((element) => element.render?.());
}

if (typeof document !== 'undefined' && typeof customElements !== 'undefined') {
  if (!customElements.get('em-first-paragraph')) {
    customElements.define('em-first-paragraph', EmFirstParagraph);
  }

  const editorListenerKey = '__emFirstParagraphSectionListener';
  if (!globalThis[editorListenerKey]) {
    document.addEventListener('shopify:section:load', (event) => {
      initializeFirstParagraphs(event.target);
    });
    globalThis[editorListenerKey] = true;
  }
}
